import { useState, useEffect, useRef } from "react";
import { Modal, Spin } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  CloseCircleOutlined,
  ArrowLeftOutlined,
  LoadingOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { checkTableName } from "../../../../../api/bills";
import { reservationToConfirm } from "./tableReservationNotice";
import { useReservationConfirm } from "./useReservationConfirm";

import { getApiErrorMessage } from "../../../../../utils/translations";
import { useViewport } from "../../../../../hooks/useViewport";
import { R } from "../../orderingTheme";
import {
  ModalHeader,
  GhostButton,
  PrimaryButton,
  footerButtonHeight,
} from "../../modalPrimitives";

interface NewTableModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (tableName: string) => void;
  /** Заето име НЕ е грешка (разделяне): проверката при кого е заета масата
   * зависи от целевия сервитьор, който се избира СЛЕД този модал — затова тук
   * не проверяваме, а BE-то отговаря при самото разделяне и FE-то пита
   * „прехвърляне към съществуващата маса?". */
  allowExistingName?: boolean;
}

const keypadRows = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
];

/**
 * Отговорил ли е ИЗОБЩО облакът. Мрежова грешка (спрял интернет, таймаут,
 * DNS) няма `response` — тогава съобщението е за връзката, а не за масата.
 * Отговорил 4xx носи `detail` от BE-то и то се показва дословно.
 */
const hasApiDetail = (error: unknown): boolean => {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return false;
  }
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    .response?.data?.detail;
  return detail !== undefined && detail !== null && detail !== "";
};

/**
 * Счупена ли е САМАТА проверка, при жива мрежа: 404 (маршрутът го няма — облак
 * или каса отпреди него) и 5xx (гръмнал е вътре). Само тези два отговора значат
 * „питахме и не получихме отговор на въпроса"; всичко друго е или мълчаща
 * мрежа, или отговор по същество, който трябва да се покаже.
 */
const theCheckRouteItselfIsBroken = (error: unknown): boolean => {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === "number" && (status === 404 || status >= 500);
};

/** Какво да се каже в скобите на предупреждението — кодът, ако го има. */
const reasonOf = (error: unknown): string => {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === "number" ? String(status) : "";
};

export const NewTableModal = ({
  open,
  onClose,
  onSubmit,
  allowExistingName = false,
}: NewTableModalProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  // „Масата има резервация — продължаваш ли?" (модал с Отказ/Продължи)
  const { confirmNotice, reservationConfirmModal } = useReservationConfirm();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const [tableName, setTableName] = useState("");
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const submitRef = useRef<() => void>(() => {});
  const cancelRef = useRef<() => void>(() => {});

  // Текстът на „облакът не отговори" е константа в рендера, за да служи и за
  // РАЗПОЗНАВАНЕ: под него се показва редът какво може да направи управител.
  // Така няма второ състояние, което да се чисти на всяко натискане.
  const offlineError = t("ordering.tableCheckOffline", {
    defaultValue:
      "Няма връзка с облака — масата не е създадена. Провери интернета и опитай пак.",
  });

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        submitRef.current();
        return;
      }
      if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
        e.preventDefault();
        cancelRef.current();
        return;
      }
      // Фокус в самото поле: символите и Backspace ги пише input-ът през
      // onChange — без този guard всеки знак би влизал двойно.
      if (e.target instanceof HTMLInputElement) return;
      if (
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        /^[\p{L}\p{N} .-]$/u.test(e.key)
      ) {
        setTableName((prev) => prev + e.key);
        if (error) setError("");
      } else if (e.key === "Backspace") {
        setTableName((prev) => prev.slice(0, -1));
        if (error) setError("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, error]);

  const resetState = () => {
    setTableName("");
    setError("");
    setIsChecking(false);
  };

  const handleNumberClick = (num: string) => {
    setTableName((prev) => prev + num);
    if (error) setError("");
  };

  const handleBackspace = () => {
    setTableName((prev) => prev.slice(0, -1));
    if (error) setError("");
  };

  const handleClear = () => {
    setTableName("");
    setError("");
  };

  submitRef.current = async () => {
    const trimmed = tableName.trim();
    if (!trimmed) {
      setError(t("ordering.tableNumber"));
      return;
    }
    if (isChecking) return;

    if (allowExistingName) {
      // Разделяне: заетото име е прехвърляне (не се проверява тук), но нова
      // маса с резервация се пита. Провалена проверка не спира разделянето.
      setIsChecking(true);
      let proceed = true;
      try {
        const notice = reservationToConfirm(await checkTableName(trimmed));
        setIsChecking(false);
        if (notice) proceed = await confirmNotice(trimmed, notice);
      } catch {
        // при съмнение обслужвай — питането за резервация е удобство
      } finally {
        setIsChecking(false);
      }
      if (!proceed) return;
      setError("");
      onSubmit(trimmed);
      resetState();
      onClose();
      return;
    }

    setIsChecking(true);
    try {
      const { exists, reservation } = await checkTableName(trimmed);
      if (exists) {
        setError(t("ordering.tableAlreadyExists", { name: trimmed }));
        return;
      }
      // Масата е заета от резервация (настанена на съседна маса / предстояща
      // скоро) — модал „Продължаваш ли?"; „Отказ" оставя „Нова маса" отворен.
      if (reservation) {
        setIsChecking(false);
        if (!(await confirmNotice(trimmed, reservation))) return;
      }
      setError("");
      onSubmit(trimmed);
      resetState();
      onClose();
    } catch (err) {
      // Мълчаливият catch (само console.error) беше докладваният дефект: при
      // спрял интернет въртележката се въртеше до таймаута на ОС-а, после
      // спираше и НЕ СЕ СЛУЧВАШЕ НИЩО — нито грешка, нито маса. Сега полето
      // за грешка казва какво стана, а бутонът остава активен за нов опит.
      if (theCheckRouteItselfIsBroken(err)) {
        // САМО 404 (маршрутът го няма — по-стар облак) и 5xx (сървърът гръмна
        // в него). Тогава мрежа ИМА, счупена е само проверката — а тя е
        // УДОБСТВО: заетото име се отказва от самото създаване, и в облака
        // (`features/bills/service.py`), и на касата. До 0.0.269 резервът
        // `getBills()` минаваше и масата се отваряше; след махането му екранът
        // започна да отказва (22.09.2026, находка Н7). Инвариант 15: при
        // съмнение ОБСЛУЖВАЙ.
        //
        // Всичко останало — мрежова грешка И всеки друг отговор на сървъра —
        // си остава отказ: 4xx с обяснение може да Е самият отказ („Масата е
        // заета"), а мълчаща мрежа значи, че и създаването няма да мине.
        message.warning(t("ordering.tableCheckSkipped", { reason: reasonOf(err) }));
        onSubmit(trimmed);
        resetState();
        onClose();
        return;
      }
      setError(hasApiDetail(err) ? getApiErrorMessage(err, offlineError) : offlineError);
    } finally {
      setIsChecking(false);
    }
  };

  cancelRef.current = () => {
    resetState();
    onClose();
  };

  const width = isMobile ? undefined : isTablet ? 420 : 460;
  const bodyPad = isNarrow ? "16px" : "20px 24px 24px";
  const keypadGap = isNarrow ? 10 : 12;
  const btnHeight = isNarrow ? 52 : 56;
  const slotHeight = isNarrow ? 52 : 60;

  return (
    <Modal
      open={open}
      onCancel={cancelRef.current}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{
          background: R.surface,
          fontFamily: R.font,
          color: R.text,
        }}
      >
        <ModalHeader
          icon={<TableOutlined />}
          title={t("ordering.newTable")}
          subtitle={t("ordering.tableNumber")}
          onClose={cancelRef.current}
          isNarrow={isNarrow}
        />

        {/* Body */}
        <div
          style={{
            padding: bodyPad,
            display: "flex",
            flexDirection: "column",
            gap: isNarrow ? 14 : 16,
          }}
        >
          {/* Number slot — истински input, за да излиза клавиатурата на
              телефона при докосване (САМО тогава — нарочно без autoFocus);
              екранният keypad и глобалният keydown остават другите пътища. */}
          <div
            style={{
              position: "relative",
              height: slotHeight,
              borderRadius: 12,
              border: `1px solid ${
                error ? R.danger : tableName ? R.brand : R.border
              }`,
              background: tableName ? R.brandTint : R.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: tableName ? R.brandDeep : R.textMuted,
              fontSize: 24,
              fontWeight: 700,
              fontFamily: R.mono,
              letterSpacing: 1,
              lineHeight: 1,
              transition:
                "border-color 160ms ease, background 160ms ease, color 160ms ease",
            }}
          >
            <input
              value={tableName}
              onChange={(e) => {
                // същият филтър като глобалния keydown — букви/цифри/интервал/./-
                setTableName(
                  e.target.value.replace(/[^\p{L}\p{N} .-]/gu, ""),
                );
                if (error) setError("");
              }}
              placeholder="—"
              className="h-full w-full border-0 bg-transparent text-center outline-none [font:inherit] text-inherit tracking-[inherit] placeholder:text-pos-muted"
            />
            {isChecking && (
              <span
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 18, color: R.brand }}
                      spin
                    />
                  }
                />
              </span>
            )}
          </div>

          {error && (
            <div
              role="alert"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(220,38,38,0.08)",
                border: "1px solid rgba(220,38,38,0.18)",
                color: R.danger,
                fontSize: 12.5,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {error}
              {error === offlineError && (
                // Какво да НАПРАВИ човекът, а не само какво е станало:
                // автоматичното превключване може и да не е решило, а
                // управителят има ръчния път от лентата горе.
                <div style={{ marginTop: 6, opacity: 0.85 }}>
                  {t("ordering.tableCheckOfflineHint", {
                    defaultValue:
                      "Ако интернетът е спрял, управител може да превключи обекта на работа срещу касата.",
                  })}
                </div>
              )}
            </div>
          )}

          {/* Keypad */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: keypadGap,
            }}
          >
            {keypadRows.flat().map((n) => (
              <KeypadButton
                key={n}
                label={n}
                onClick={() => handleNumberClick(n)}
                height={btnHeight}
              />
            ))}

            <KeypadButton
              aria-label={t("modules.accessModal.clear") || "Clear"}
              icon={<CloseCircleOutlined style={{ fontSize: 20 }} />}
              onClick={handleClear}
              disabled={tableName.length === 0}
              height={btnHeight}
            />
            <KeypadButton
              label="0"
              onClick={() => handleNumberClick("0")}
              height={btnHeight}
            />
            <KeypadButton
              aria-label={t("modules.accessModal.backspace") || "Backspace"}
              icon={<ArrowLeftOutlined style={{ fontSize: 20 }} />}
              onClick={handleBackspace}
              disabled={tableName.length === 0}
              height={btnHeight}
            />
          </div>

          {/* Footer actions — „Назад" тесен (1:3) на всички размери */}
          <div className="mt-1 grid grid-cols-[1fr_3fr] gap-2.5">
            <GhostButton
              label={t("common.back")}
              onClick={cancelRef.current}
              shortcut={isDesktop ? "Esc" : undefined}
              height={isNarrow ? footerButtonHeight(true) : btnHeight}
            />
            <PrimaryButton
              label={t("common.ok")}
              onClick={submitRef.current}
              disabled={!tableName.trim() || isChecking}
              shortcut={isDesktop ? "⏎" : undefined}
              height={isNarrow ? footerButtonHeight(true) : btnHeight}
            />
          </div>
        </div>
      </div>
      {reservationConfirmModal}
    </Modal>
  );
};

type KeypadButtonProps = {
  label?: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  height: number;
  "aria-label"?: string;
};

function KeypadButton({
  label,
  icon,
  onClick,
  disabled,
  height,
  ...rest
}: KeypadButtonProps) {
  return (
    <motion.button
      type="button"
      whileTap={disabled ? undefined : { scale: 0.97 }}
      transition={{ duration: 0.08 }}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      disabled={disabled}
      aria-label={rest["aria-label"]}
      style={{
        height,
        width: "100%",
        borderRadius: 12,
        background: R.surface,
        border: `1px solid ${R.border}`,
        color: R.ink,
        fontSize: 22,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition:
          "background 140ms ease, border-color 140ms ease, color 140ms ease",
        userSelect: "none",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = R.brandTint;
        e.currentTarget.style.borderColor = "rgba(217,119,6,0.32)";
        e.currentTarget.style.color = R.brandDeep;
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = R.surface;
        e.currentTarget.style.borderColor = R.border;
        e.currentTarget.style.color = R.ink;
      }}
    >
      {icon ?? label}
    </motion.button>
  );
}
